import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Target, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { api } from '../api/client';
import { useApp } from '../contexts/AppContext';
import TaskModal from './TaskModal';
import { toast, Checkbox } from './UI';

// Vertical layout — each scope is its own row, top-down hierarchy
const ROW_Y = {
  goal: 60,
  quarter: 320,
  month: 580,
  week: 580,
  key_result: 580,
  initiative: 840,
  task: 1100,
};
const NODE_SPACING_X = 320;
const START_X = 80;

const SCOPE_STYLE = {
  quarter: { bg: 'var(--bg-1)', border: 'var(--purple)', accent: 'var(--purple)', label: 'Quartalsziel', size: 'medium' },
  key_result: { bg: 'var(--bg-1)', border: 'var(--orange)', accent: 'var(--orange)', label: 'Key Result', size: 'medium' },
  month: { bg: 'var(--bg-1)', border: 'var(--blue)', accent: 'var(--blue)', label: 'Monatsziel', size: 'medium' },
  week: { bg: 'var(--bg-1)', border: 'var(--green)', accent: 'var(--green)', label: 'Wochenziel', size: 'medium' },
  task: { bg: 'var(--bg-2)', border: 'var(--border-2)', accent: 'var(--text-2)', label: 'Task', size: 'small' },
};

// ---- Custom Nodes ----------------------------------------------------------
const handleStyle = (color) => ({
  width: 12, height: 12, background: color, border: '2px solid var(--bg-1)', borderRadius: '50%',
});
const handleStyleOverlay = { width: 12, height: 12, background: 'transparent', border: 'none', borderRadius: '50%' };

function GoalNode({ data }) {
  const { goal } = data;
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 12,
      background: 'var(--bg-1)', border: `3px solid ${goal.color}`,
      minWidth: 220, maxWidth: 260, color: 'var(--text)', fontFamily: 'var(--font-sans)',
      boxShadow: 'var(--shadow)',
    }}>
      {/* Goals haben nur einen Bottom-Source (geben Hierarchie nach unten) */}
      <Handle type="source" position={Position.Bottom} id="bot-s" style={handleStyle(goal.color)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Target size={14} style={{ color: goal.color }} />
        <span style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Jahresziel · {goal.year}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{goal.title}</div>
      {goal.description && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {goal.description}
        </div>
      )}
    </div>
  );
}

function TaskNode({ data }) {
  const { isDone } = useApp();
  const { task } = data;
  const scope = task.scope || 'task';
  const style = SCOPE_STYLE[scope] || SCOPE_STYLE.task;
  const isSmall = style.size === 'small';
  const done = isDone(task);
  return (
    <div style={{
      padding: isSmall ? '6px 10px' : '10px 14px',
      borderRadius: 8,
      background: style.bg, border: `2px solid ${style.border}`,
      minWidth: isSmall ? 180 : 240,
      maxWidth: isSmall ? 220 : 280,
      color: 'var(--text)', fontFamily: 'var(--font-sans)',
      opacity: done ? 0.55 : 1,
      boxShadow: 'var(--shadow)',
    }}>
      {/* Top: Target sichtbar, Source als unsichtbarer Overlay → ziehen in beide Richtungen möglich */}
      <Handle type="target" position={Position.Top} id="top-t" style={handleStyle(style.border)} />
      <Handle type="source" position={Position.Top} id="top-s" style={handleStyleOverlay} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ fontSize: 9, color: style.accent, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
          {style.label}
        </span>
        {scope === 'quarter' || scope === 'key_result' ? (
          <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>
            {task.progress || 0}%
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: isSmall ? 12 : 13, fontWeight: 500, textDecoration: done ? 'line-through' : 'none' }}>
        {task.title}
      </div>
      {!isSmall && task.next_step && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          → {task.next_step}
        </div>
      )}
      {/* Bottom: Source sichtbar, Target als unsichtbarer Overlay → ziehen in beide Richtungen möglich */}
      <Handle type="source" position={Position.Bottom} id="bot-s" style={handleStyle(style.border)} />
      <Handle type="target" position={Position.Bottom} id="bot-t" style={handleStyleOverlay} />
    </div>
  );
}

const nodeTypes = { goal: GoalNode, task: TaskNode };

// ---- Layout helper ---------------------------------------------------------
// Vertical: each scope = own row (Y), nodes within scope spread horizontally
function buildDefaultPositions(goals, tasks, scopeFilter) {
  const buckets = { goal: [], quarter: [], key_result: [], month: [], week: [], initiative: [], task: [] };
  goals.forEach(g => buckets.goal.push({ kind: 'goal', id: g.id }));
  tasks.forEach(t => {
    const scope = t.scope || 'task';
    if (!scopeFilter[scope === 'task' ? (t.parent_id ? 'initiative' : 'task') : scope]) return;
    if (scope === 'task') {
      if (t.parent_id) buckets.initiative.push({ kind: 'task', id: t.id });
      else buckets.task.push({ kind: 'task', id: t.id });
    } else if (buckets[scope]) {
      buckets[scope].push({ kind: 'task', id: t.id });
    }
  });
  const positions = {};
  for (const rowName of Object.keys(buckets)) {
    const items = buckets[rowName];
    const y = ROW_Y[rowName];
    items.forEach((it, i) => {
      positions[`${it.kind}-${it.id}`] = {
        x: START_X + i * NODE_SPACING_X,
        y,
      };
    });
  }
  return positions;
}

// ---- Inner Canvas ----------------------------------------------------------
function CanvasInner() {
  const { tasks, refreshTasks } = useApp();
  const [goals, setGoals] = useState([]);
  const [savedPositions, setSavedPositions] = useState({}); // key: "type-id" → {x,y}
  const [dependencies, setDependencies] = useState([]);
  const [editTaskId, setEditTaskId] = useState(null);
  const [scopeFilter, setScopeFilter] = useState({
    goal: true, quarter: true, key_result: true, initiative: true,
    month: false, week: false, task: false,
  });
  const [showOnlyConnected, setShowOnlyConnected] = useState(false);

  const pendingPositionsRef = useRef({});
  const saveTimerRef = useRef(null);

  // Initial fetch — goals + canvas (positions, deps)
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getGoals(), api.getCanvas()])
      .then(([goalsRes, canvas]) => {
        if (cancelled) return;
        setGoals(Array.isArray(goalsRes) ? goalsRes : []);
        const posMap = {};
        for (const p of canvas.positions || []) {
          posMap[`${p.node_type}-${p.node_id}`] = { x: p.x, y: p.y };
        }
        setSavedPositions(posMap);
        setDependencies(canvas.dependencies || []);
      })
      .catch(err => toast(err.message || 'Canvas konnte nicht geladen werden', 'error'));
    return () => { cancelled = true; };
  }, []);

  // Build nodes + edges
  const { nodes, edges } = useMemo(() => {
    const defaults = buildDefaultPositions(goals, tasks, scopeFilter);
    const allNodes = [];
    const goalIds = new Set();

    if (scopeFilter.goal) {
      for (const g of goals) {
        const key = `goal-${g.id}`;
        const pos = savedPositions[key] || defaults[key] || { x: 0, y: 0 };
        allNodes.push({ id: key, type: 'goal', position: pos, data: { goal: g } });
        goalIds.add(g.id);
      }
    }

    const taskIdToNodeId = {};
    for (const t of tasks) {
      const scope = t.scope || 'task';
      // Filter
      if (scope === 'quarter' && !scopeFilter.quarter) continue;
      if (scope === 'key_result' && !scopeFilter.key_result) continue;
      if (scope === 'month' && !scopeFilter.month) continue;
      if (scope === 'week' && !scopeFilter.week) continue;
      if (scope === 'task') {
        if (t.parent_id) {
          if (!scopeFilter.initiative) continue;
        } else {
          if (!scopeFilter.task) continue;
        }
      }
      const key = `task-${t.id}`;
      const pos = savedPositions[key] || defaults[key] || { x: 0, y: 0 };
      allNodes.push({ id: key, type: 'task', position: pos, data: { task: t } });
      taskIdToNodeId[t.id] = key;
    }

    // Edges
    const allEdges = [];
    // Hierarchy: goal → quarter
    for (const t of tasks) {
      if (t.scope === 'quarter' && t.goal_id && goalIds.has(t.goal_id) && taskIdToNodeId[t.id]) {
        allEdges.push({
          id: `goal-${t.goal_id}-${t.id}`,
          source: `goal-${t.goal_id}`,
          target: `task-${t.id}`,
          type: 'smoothstep',
          style: { stroke: 'var(--accent)', strokeWidth: 2 },
          deletable: false,
          selectable: false,
        });
      }
    }
    // Hierarchy: parent_id between tasks
    for (const t of tasks) {
      if (t.parent_id && taskIdToNodeId[t.id] && taskIdToNodeId[t.parent_id]) {
        allEdges.push({
          id: `parent-${t.parent_id}-${t.id}`,
          source: `task-${t.parent_id}`,
          target: `task-${t.id}`,
          type: 'smoothstep',
          style: { stroke: 'var(--accent)', strokeWidth: 2 },
          deletable: false,
          selectable: false,
        });
      }
    }
    // Dependencies
    for (const d of dependencies) {
      if (taskIdToNodeId[d.from_task_id] && taskIdToNodeId[d.to_task_id]) {
        allEdges.push({
          id: `dep-${d.id || `${d.from_task_id}-${d.to_task_id}`}`,
          source: `task-${d.from_task_id}`,
          target: `task-${d.to_task_id}`,
          type: 'smoothstep',
          animated: true,
          style: { stroke: 'var(--orange)', strokeWidth: 2, strokeDasharray: '6 4' },
          data: { kind: 'dependency', depId: d.id, from: d.from_task_id, to: d.to_task_id },
          deletable: true,
        });
      }
    }

    let filteredNodes = allNodes;
    let filteredEdges = allEdges;
    if (showOnlyConnected) {
      const referenced = new Set();
      for (const e of allEdges) { referenced.add(e.source); referenced.add(e.target); }
      filteredNodes = allNodes.filter(n => referenced.has(n.id));
      const visIds = new Set(filteredNodes.map(n => n.id));
      filteredEdges = allEdges.filter(e => visIds.has(e.source) && visIds.has(e.target));
    }

    return { nodes: filteredNodes, edges: filteredEdges };
  }, [goals, tasks, savedPositions, dependencies, scopeFilter, showOnlyConnected]);

  // Position persistence (debounced batch)
  const flushPositions = useCallback(async () => {
    const pending = pendingPositionsRef.current;
    pendingPositionsRef.current = {};
    const items = Object.entries(pending).map(([key, pos]) => {
      const [node_type, ...rest] = key.split('-');
      return { node_type, node_id: rest.join('-'), x: pos.x, y: pos.y };
    });
    if (items.length === 0) return;
    try {
      await api.saveCanvasPositions(items);
    } catch (err) {
      toast(err.message || 'Position konnte nicht gespeichert werden', 'error');
    }
  }, []);

  const queueSave = useCallback((nodeId, position) => {
    pendingPositionsRef.current[nodeId] = position;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushPositions, 600);
  }, [flushPositions]);

  const onNodesChange = useCallback((changes) => {
    // Apply visual changes to local state, persist position changes
    setSavedPositions(prev => {
      const next = { ...prev };
      let mutated = false;
      for (const ch of changes) {
        if (ch.type === 'position' && ch.position) {
          next[ch.id] = ch.position;
          mutated = true;
          if (ch.dragging === false) {
            queueSave(ch.id, ch.position);
          }
        }
      }
      return mutated ? next : prev;
    });
  }, [queueSave]);

  const onEdgesChange = useCallback((changes) => {
    // Handle deletions of dependency edges
    for (const ch of changes) {
      if (ch.type === 'remove' && ch.id?.startsWith('dep-')) {
        const dep = dependencies.find(d => `dep-${d.id}` === ch.id);
        if (dep && dep.id) {
          api.deleteDependency({ id: dep.id })
            .then(() => setDependencies(prev => prev.filter(d => d.id !== dep.id)))
            .catch(err => toast(err.message || 'Dependency konnte nicht gelöscht werden', 'error'));
        }
      }
    }
  }, [dependencies]);

  const onConnect = useCallback(async (connection) => {
    // Only allow task→task connections (not goal-source which is hierarchy only)
    if (!connection.source?.startsWith('task-') || !connection.target?.startsWith('task-')) return;
    const fromId = connection.source.replace('task-', '');
    const toId = connection.target.replace('task-', '');
    if (fromId === toId) return;
    // Don't duplicate hierarchy edges
    const fromTask = tasks.find(t => t.id === fromId);
    const toTask = tasks.find(t => t.id === toId);
    if (toTask?.parent_id === fromId) return; // already a parent-child
    try {
      const dep = await api.createDependency({ from_task_id: fromId, to_task_id: toId });
      setDependencies(prev => [...prev, dep]);
    } catch (err) {
      toast(err.message || 'Dependency konnte nicht erstellt werden', 'error');
    }
  }, [tasks]);

  const onNodeClick = useCallback((event, node) => {
    if (node.type === 'task') setEditTaskId(node.data.task.id);
    // Goal click: ignore for now (could open goal-edit modal)
  }, []);

  const editTask = editTaskId ? tasks.find(t => t.id === editTaskId) : null;

  const resetLayout = async () => {
    try {
      await api.resetCanvasPositions();
      setSavedPositions({});
      toast('Layout zurückgesetzt', 'success');
    } catch (err) {
      toast(err.message || 'Reset fehlgeschlagen', 'error');
    }
  };

  const FilterToggle = ({ k, label, color }) => (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', cursor: 'pointer',
      fontSize: 12, color: 'var(--text-2)',
    }}>
      <Checkbox checked={scopeFilter[k]} onChange={() => setScopeFilter(f => ({ ...f, [k]: !f[k] }))} />
      {color && <span style={{ width: 8, height: 8, background: color, borderRadius: 2 }} />}
      <span>{label}</span>
    </label>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Side-Panel */}
      <div style={{
        width: 220, borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', background: 'var(--bg-1)',
        padding: 12, gap: 14,
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Sichtbarkeit</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FilterToggle k="goal" label="Jahresziele" />
            <FilterToggle k="quarter" label="Quartalsziele" color="var(--purple)" />
            <FilterToggle k="key_result" label="Key Results" color="var(--orange)" />
            <FilterToggle k="initiative" label="Initiativen" />
            <FilterToggle k="month" label="Monatsziele" color="var(--blue)" />
            <FilterToggle k="week" label="Wochenziele" color="var(--green)" />
            <FilterToggle k="task" label="Lose Tasks (ohne Parent)" />
          </div>
        </div>

        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowOnlyConnected(v => !v)} style={{ width: '100%' }}>
            {showOnlyConnected ? <Eye size={12} /> : <EyeOff size={12} />}
            {showOnlyConnected ? 'Alle anzeigen' : 'Nur verbundene'}
          </button>
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={resetLayout} title="Alle gespeicherten Positionen löschen">
            <RotateCcw size={12} /> Layout zurücksetzen
          </button>
          <p style={{ fontSize: 10, color: 'var(--text-3)', lineHeight: 1.5 }}>
            <strong>Hierarchie</strong> oben → unten: Jahresziele · Quartale · KRs/Monat/Woche · Initiativen.<br /><br />
            <strong>Zoom</strong>: Mausrad oder Buttons unten links (+/− und „Fit“).<br /><br />
            <strong>Pan</strong>: leeren Bereich ziehen.<br /><br />
            <strong>Verbinden</strong>: am unteren Rand eines Task-Knotens anfassen und auf einen anderen Task ziehen → gestrichelte Abhängigkeit.<br /><br />
            <strong>Hierarchie-Linien</strong> (durchgezogen) sind nicht editierbar — die kommen aus parent_id / goal_id.<br /><br />
            <strong>Klick</strong> auf einen Task-Knoten öffnet das Task-Detail.
          </p>
        </div>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative', height: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
          deleteKeyCode={['Backspace', 'Delete']}
          minZoom={0.15}
          maxZoom={2.5}
          panOnScroll={false}
          zoomOnScroll
          zoomOnPinch
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={24} size={1} color="var(--border)" />
          <Controls showZoom showFitView showInteractive position="bottom-left" />
          <MiniMap nodeStrokeColor={(n) => {
            if (n.type === 'goal') return n.data?.goal?.color || '#888';
            const sc = n.data?.task?.scope || 'task';
            return SCOPE_STYLE[sc]?.border || '#888';
          }} pannable zoomable position="bottom-right" />
        </ReactFlow>
      </div>

      {editTask && (
        <TaskModal
          task={editTask}
          onClose={() => setEditTaskId(null)}
          onSaved={refreshTasks}
          onNavigate={(t) => setEditTaskId(t?.id || null)}
        />
      )}
    </div>
  );
}

export default function CanvasView() {
  return (
    <ReactFlowProvider>
      <CanvasInner />
    </ReactFlowProvider>
  );
}
